import type { ReactNode } from 'react';
import { SiClaude, SiOpenai } from 'react-icons/si';

/** Same marks as the connection guide on Settings → MCP. */
const CURSOR_LOGO_SRC = '/cursor-logo.svg';

export type McpClientBrandId = 'claude' | 'codex' | 'cursor';

const BRAND_LABELS: Record<McpClientBrandId, string> = {
  claude: 'Claude',
  codex: 'Codex',
  cursor: 'Cursor'
};

function hostnameOf(redirectUri: string): string {
  try {
    return new URL(redirectUri).hostname.toLowerCase();
  } catch {
    return '';
  }
}

function hostIs(host: string, root: string): boolean {
  return host === root || host.endsWith(`.${root}`);
}

/**
 * Pick a known client mark from the redirect host, then the self-reported name.
 * Unknown clients stay unmarked so the consent screen does not invent a brand.
 */
export function detectMcpClientBrand(
  clientName: string,
  redirectUri: string
): McpClientBrandId | null {
  const host = hostnameOf(redirectUri);
  const name = clientName.trim().toLowerCase();

  if (hostIs(host, 'claude.ai') || name.includes('claude')) return 'claude';
  if (
    hostIs(host, 'chatgpt.com') ||
    name.includes('codex') ||
    name.includes('chatgpt')
  ) {
    return 'codex';
  }
  if (hostIs(host, 'cursor.com') || name.includes('cursor')) return 'cursor';
  return null;
}

export function mcpClientBrandLabel(brand: McpClientBrandId): string {
  return BRAND_LABELS[brand];
}

export function McpClientBrandMark({
  brand,
  size = 18
}: {
  brand: McpClientBrandId;
  size?: number;
}): ReactNode {
  if (brand === 'claude') {
    return <SiClaude className="text-primary" size={size} aria-hidden />;
  }
  if (brand === 'codex') {
    return <SiOpenai className="text-primary" size={size} aria-hidden />;
  }
  return (
    <img
      src={CURSOR_LOGO_SRC}
      alt=""
      width={size}
      height={size}
      aria-hidden
      style={{ objectFit: 'contain' }}
    />
  );
}
